import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { SECTION } from 'src/constants/section.ts';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import Accordion from 'src/components/common/Accordion';
import AccordionItem from 'src/components/common/Accordion/components/AccordionItem';
import AccordionItemHeader from 'src/components/common/Accordion/components/AccordionItemHeader';
import AccordionItemPanel from 'src/components/common/Accordion/components/AccordionItemPanel';
import FilterDisplay from 'src/components/common/FilterDisplay';
import OrgTree from 'src/components/common/OrgTree';
import { orgUnitToFilter } from 'src/components/common/OrgTree/helper';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import PageFrame from 'src/components/common/PageFrame';
import OrgUnitHeader from 'src/components/planAction/OrgUnitHeader';
import PlanActionActionBanner from 'src/components/planAction/PlanActionActionBanner';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { translateFilters } from 'src/components/planAction/PlanActionStatisticsPage/helpers';
import PlanActionTable from 'src/components/planAction/PlanActionTable';

import FollowUp from './FollowUp';

const PlanActionOverviewPage = () => {
  const { t } = useTranslation('report');
  const { reset: resetFilter } = useFilterContext();
  const { selectedOrgUnit, orgTreeState } = usePlanActionContext();

  const formatLabel = useCallback(
    (name, values) => {
      const value = values[0];
      if (!value) return '';
      if (name === 'orgUnit') {
        return `${value.label}`;
      }
      const translatedText = translateFilters(value.filterExpr[name], t);
      return `${value.label}: ${translatedText || value.filterExpr[name]}`;
    },
    [t],
  );

  useEffect(() => {
    if (selectedOrgUnit) {
      resetFilter({
        defaultFilterGroups: {
          [ORG_UNIT_FILTER_NAME]: [orgUnitToFilter(selectedOrgUnit)],
        },
        filterSpecs: [
          {
            name: 'orgUnit',
            multiple: false,
            required: true,
          },
          {
            name: new RegExp('.*'),
            multiple: true,
            required: false,
          },
        ],
      });
    }
  }, [selectedOrgUnit]);

  return (
    <PageFrame
      actionBanner={<PlanActionActionBanner />}
      className="PlanActionOverviewPage-root"
      sidebar={
        <Accordion initiallyActiveItems={['plan-action-org-filter']}>
          <AccordionItem itemName="plan-action-org-filter">
            <AccordionItemHeader headline={t('sidebar.title')} />
            <AccordionItemPanel>
              <OrgTree
                orgTreeState={orgTreeState}
                moduleId={SECTION.PLAN_ACTION}
              />
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>
      }
    >
      <Box>
        <FilterDisplay formatLabel={formatLabel} readonly />

        <OrgUnitHeader withBackToActionList={false} />

        <FollowUp currentOrgUnit={selectedOrgUnit} />

        <PlanActionTable />
      </Box>
    </PageFrame>
  );
};

export default PlanActionOverviewPage;
